/**
 * Phase D: Hook for Issuer Needs-Split Review UI
 * Reads from v_issuer_needs_split, writes to merchant_issuer_needs_split
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface IssuerNeedsSplitRow {
  id: string;
  brand_raw: string;
  brand_normalized: string;
  occurrences: number;
  status: string;
  parent_merchant_id: string | null;
  parent_merchant_name: string | null;
  parent_merchant_key: string | null;
  sample_deal_ids: string[] | null;
  sample_headlines: string[] | null;
  sample_promo_merchant_keys: string[] | null;
  last_seen_at: string | null;
  created_at: string | null;
  updated_at: string | null;
}

const QUERY_KEY = ["issuer-needs-split"];

export function useIssuerNeedsSplitList() {
  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: async (): Promise<IssuerNeedsSplitRow[]> => {
      const { data, error } = await supabase
        .from("v_issuer_needs_split")
        .select("*");

      if (error) throw error;
      return (data ?? []) as unknown as IssuerNeedsSplitRow[];
    },
  });
}

export function useIssuerNeedsSplitMutations() {
  const qc = useQueryClient();

  const updateStatus = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const { error } = await supabase
        .from("merchant_issuer_needs_split")
        .update({ status, updated_at: new Date().toISOString() })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QUERY_KEY });
      toast.success("Status updated");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const setParent = useMutation({
    mutationFn: async ({
      id,
      parent_merchant_id,
    }: {
      id: string;
      parent_merchant_id: string;
    }) => {
      const { error } = await supabase
        .from("merchant_issuer_needs_split")
        .update({
          parent_merchant_id,
          status: "resolved",
          updated_at: new Date().toISOString(),
        })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QUERY_KEY });
      toast.success("Parent mapped & status set to resolved");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  return { updateStatus, setParent };
}

/** Preview how many deals would be updated by applying parent mapping */
export function useIssuerApplyPreview() {
  const qc = useQueryClient();

  const preview = useMutation({
    mutationFn: async (needsSplitId: string) => {
      const { data, error } = await supabase.rpc(
        "rpc_preview_issuer_parent_mapping" as any,
        { p_needs_split_id: needsSplitId }
      );
      if (error) throw error;
      return data as {
        brand_raw: string;
        parent_merchant_id: string;
        parent_merchant_name: string;
        status: string;
        deals_to_update: number;
      };
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const apply = useMutation({
    mutationFn: async (needsSplitId: string) => {
      const { data, error } = await supabase.rpc(
        "rpc_apply_issuer_parent_mapping" as any,
        { p_needs_split_id: needsSplitId }
      );
      if (error) throw error;
      return data as {
        success: boolean;
        brand_raw: string;
        parent_merchant_id: string;
        deals_updated: number;
      };
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: QUERY_KEY });
      toast.success(`Applied: ${(data as any).deals_updated} deal(s) updated`);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const revert = useMutation({
    mutationFn: async (needsSplitId: string) => {
      const { data, error } = await supabase.rpc(
        "rpc_revert_issuer_parent_mapping" as any,
        { p_needs_split_id: needsSplitId }
      );
      if (error) throw error;
      return data as {
        success: boolean;
        brand_raw: string;
        parent_merchant_id: string;
        deals_reverted: number;
      }[];
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: QUERY_KEY });
      const row = Array.isArray(data) ? data[0] : data;
      toast.success(`Reverted: ${(row as any)?.deals_reverted ?? 0} deal(s) cleared`);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  return { preview, apply, revert };
}

/** Search merchants that can serve as issuers */
export function useIssuerMerchantSearch(search: string) {
  return useQuery({
    queryKey: ["issuer-merchant-search", search],
    queryFn: async () => {
      let query = supabase
        .from("merchants")
        .select("id, merchant_key, display_name, merchant_type")
        .in("merchant_type", ["issuer", "both"])
        .order("display_name")
        .limit(50);

      if (search.trim()) {
        // Sanitize: strip chars that break PostgREST .or() filter syntax
        const safe = search.replace(/[,()%\\]/g, "").trim();
        if (safe.length > 0) {
          query = query.or(
            `display_name.ilike.%${safe}%,merchant_key.ilike.%${safe}%`
          );
        }
      }

      const { data, error } = await query;
      if (error) throw error;
      return data ?? [];
    },
    enabled: true,
  });
}
